import type { Device, Flow, FlowFilters, MetricsRange, ThroughputResult } from '../../api/types';
import { formatBytes, formatMbit, toMbit } from '../../lib/format';
import { vt } from '../../theme';

export const RANGES: { id: MetricsRange; label: string }[] = [
  { id: '1h', label: '1 h' },
  { id: '24h', label: '24 h' },
  { id: '7d', label: '7 d' },
  { id: '30d', label: '30 d' },
];

/** The mockup's band order: cyan, brand, green, violet; everything past them is one neutral "other". */
export const BAND_COLORS = [vt.cyan, vt.brand, vt.green, vt.violet];
export const OTHER_COLOR = vt.fg4;
export const OTHER = 'other';
export const TOP_BANDS = BAND_COLORS.length;

export interface Band {
  /** The device id, or OTHER. */
  key: string;
  name: string;
  color: string;
  /** Mbit/s per point, rx + tx. */
  mbit: number[];
  /** Bytes over the whole window. */
  bytes: number;
}

export interface Stacked {
  bands: Band[];
  /** Epoch ms. */
  from: number;
  to: number;
  stepSeconds: number;
  points: number;
  /** The highest stacked point; null when nothing moved. `at` is the start of its bucket (epoch ms). */
  peak: { mbit: number; at: number } | null;
  /** Bytes the devices received / sent over the window, summed over every series. */
  rx: number;
  tx: number;
}

const sum = (xs: readonly number[]) => xs.reduce((a, v) => a + v, 0);

/**
 * GET /metrics/throughput?by=device as chart bands: the TOP_BANDS devices with the most bytes,
 * coloured in the mockup's order, then one "other" band holding the rest (left out when empty).
 */
export function toBands(result: ThroughputResult, top = TOP_BANDS): Stacked {
  const { stepSeconds, points } = result;
  const series = result.series
    .map((s) => ({ s, bytes: sum(s.rx) + sum(s.tx) }))
    .filter((x) => x.bytes > 0)
    .sort((a, b) => b.bytes - a.bytes || a.s.name.localeCompare(b.s.name));
  const point = (rx: number[], tx: number[], i: number) => toMbit((rx[i] ?? 0) + (tx[i] ?? 0), stepSeconds);

  const bands: Band[] = series.slice(0, top).map(({ s, bytes }, i) => ({
    key: s.deviceId ?? s.name,
    name: s.name,
    color: BAND_COLORS[i]!,
    mbit: Array.from({ length: points }, (_, j) => point(s.rx, s.tx, j)),
    bytes,
  }));
  const rest = series.slice(top);
  if (rest.length > 0) {
    bands.push({
      key: OTHER,
      name: OTHER,
      color: OTHER_COLOR,
      mbit: Array.from({ length: points }, (_, j) => sum(rest.map(({ s }) => point(s.rx, s.tx, j)))),
      bytes: sum(rest.map((x) => x.bytes)),
    });
  }

  const from = Date.parse(result.from);
  let peak: Stacked['peak'] = null;
  for (let i = 0; i < points; i++) {
    const v = sum(bands.map((b) => b.mbit[i]!));
    if (v > 0 && (!peak || v > peak.mbit)) peak = { mbit: v, at: from + i * stepSeconds * 1000 };
  }
  return {
    bands,
    from,
    to: Date.parse(result.to),
    stepSeconds,
    points,
    peak,
    rx: sum(result.series.map((s) => sum(s.rx))),
    tx: sum(result.series.map((s) => sum(s.tx))),
  };
}

/** The smallest step of 1, 2, 2.5 or 5 × 10^k that reaches `max` in `ticks` steps. */
function niceStep(max: number, ticks: number): number {
  const mag = 10 ** Math.floor(Math.log10(max / ticks));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s * ticks >= max - 1e-9)!;
}

/**
 * A y axis of 3–5 even steps of 1, 2, 2.5 or 5 × 10^k: the lowest such top at or above `max`,
 * so the data fills the plot (4.3 → 0–5 in five steps, not 0–8 in four). Ties keep four steps.
 */
export function niceScale(max: number): { max: number; step: number; ticks: number } {
  if (!(max > 0)) return { max: 4, step: 1, ticks: 4 };
  let best = { max: Infinity, step: 1, ticks: 4 };
  for (const ticks of [4, 3, 5]) {
    const step = niceStep(max, ticks);
    if (step * ticks < best.max - 1e-9) best = { max: step * ticks, step, ticks };
  }
  return best;
}

/** "2", "0.5", "2.5": a tick value without float noise. */
export const tickLabel = (v: number) => String(Number(v.toPrecision(3)));

const hhmm = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const dayMonth = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/** "14:38" within a day's range; "Tue 14:00" on 7 d; "3 Oct" on 30 d. */
export function timeLabel(ms: number, range: MetricsRange): string {
  const d = new Date(ms);
  if (range === '7d') return `${weekday.format(d)} ${hhmm.format(d)}`;
  if (range === '30d') return dayMonth.format(d);
  return hhmm.format(d);
}

/** `count` evenly spaced x labels from `from` to `to`; `at` is 0..1 along the axis. */
export function xLabels(chart: Pick<Stacked, 'from' | 'to'>, range: MetricsRange, count = 5) {
  return Array.from({ length: count }, (_, i) => {
    const at = i / (count - 1);
    return { at, label: timeLabel(chart.from + at * (chart.to - chart.from), range) };
  });
}

/** "peak 7.4 Mbit/s · 14:38"; on 30 d the day joins the time. */
export function peakText(chart: Pick<Stacked, 'peak'>, range: MetricsRange): string {
  if (!chart.peak) return 'no traffic in this range';
  const when = range === '30d' ? `${dayMonth.format(chart.peak.at)} ${hhmm.format(chart.peak.at)}` : timeLabel(chart.peak.at, range);
  return `peak ${formatMbit(chart.peak.mbit)} · ${when}`;
}

/** The band colour a device has on the chart, so the top talkers read as the same devices. */
export function colorOf(chart: Pick<Stacked, 'bands'> | null, deviceId: string): string {
  return chart?.bands.find((b) => b.key === deviceId)?.color ?? OTHER_COLOR;
}

// ---------------------------------------------------------------------------------------
// Flows
// ---------------------------------------------------------------------------------------

export type FlowState = 'live' | 'allowed' | 'denied';

export interface FlowRow {
  id: string;
  state: FlowState;
  source: string;
  sourceIp: string;
  destination: string;
  destinationIp: string;
  protocol: string;
  port: string;
  bytes: string;
  duration: string;
  /** "#3", or null for the default rule, an isolated network or an unmatched flow. */
  ruleNumber: string | null;
  ruleText: string;
}

/** "14m 02s", "58s", "1h 05m"; an en dash for 0 (one packet, or a denied tuple). */
export function formatDuration(seconds: number): string {
  if (!(seconds > 0)) return '–';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (s < 3600) return `${Math.floor(s / 60)}m ${pad(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${pad(Math.floor((s % 3600) / 60))}m`;
}

/** The rule a flow matched: `#N comment`, `default`, `isolated network <id>`, or the bare id when it left the policy. */
export function ruleLabel(f: Pick<Flow, 'ruleId' | 'ruleNumber' | 'ruleComment'>): { number: string | null; text: string } {
  const id = f.ruleId;
  if (id === null) return { number: null, text: '–' };
  if (id === 'default') return { number: null, text: 'default' };
  if (id.startsWith('isolated:')) return { number: null, text: `isolated network ${id.slice('isolated:'.length)}` };
  if (f.ruleNumber !== null) return { number: `#${f.ruleNumber}`, text: f.ruleComment ?? '' };
  return { number: null, text: id };
}

export function flowState(f: Pick<Flow, 'result' | 'live'>): FlowState {
  return f.result === 'denied' ? 'denied' : f.live ? 'live' : 'allowed';
}

export function toFlowRow(f: Flow): FlowRow {
  const rule = ruleLabel(f);
  return {
    id: f.id,
    state: flowState(f),
    source: f.src.name ?? f.src.ip,
    sourceIp: f.src.ip,
    destination: f.dst.name ?? f.dst.ip,
    destinationIp: f.dst.ip,
    protocol: f.proto.toUpperCase(),
    port: f.port === null ? '–' : String(f.port),
    bytes: formatBytes(f.bytes),
    duration: f.result === 'denied' ? '–' : formatDuration(f.durationSeconds),
    ruleNumber: rule.number,
    ruleText: rule.text,
  };
}

/** "6 of 212 flows · 41 denied in the last 24 h". */
export function flowFooter(shown: number, total: number, denied: number): string {
  return `${shown} of ${total} flow${total === 1 ? '' : 's'} · ${denied} denied in the last 24 h`;
}

// ---------------------------------------------------------------------------------------
// The filter box
// ---------------------------------------------------------------------------------------

export interface ParsedFilter {
  filters: Omit<FlowFilters, 'limit'>;
  /** One per word that is not a device, port, rule or result; the query is not sent while any remain. */
  errors: string[];
}

const RULE_ID = /^(r_[0-9a-f]{8}|default|isolated:[a-z0-9-]{1,32})$/;

/**
 * The flow filter box, one word per filter, combined with AND as GET /flows does:
 * a device name or id (optionally `dev:`) → device; a number → port; `#N` (active policy
 * position), `r_…`, `default` or `isolated:<network>` → rule; `allowed` / `denied` → result.
 */
export function parseFilter(text: string, devices: readonly Device[], ruleIds: readonly string[]): ParsedFilter {
  const filters: ParsedFilter['filters'] = {};
  const errors: string[] = [];
  const set = <K extends keyof ParsedFilter['filters']>(key: K, value: ParsedFilter['filters'][K], word: string) => {
    if (filters[key] !== undefined && filters[key] !== value) errors.push(`Only one ${key}: "${word}"`);
    else filters[key] = value;
  };

  for (const word of text.trim().split(/\s+/).filter(Boolean)) {
    const lower = word.toLowerCase();
    const number = /^#(\d+)$/.exec(word);
    if (number) {
      const id = ruleIds[Number(number[1]) - 1];
      if (id) set('rule', id, word);
      else errors.push(`No rule ${word} in the active policy`);
    } else if (RULE_ID.test(lower)) {
      set('rule', lower, word);
    } else if (/^\d+$/.test(word)) {
      if (Number(word) <= 65535) set('port', Number(word), word);
      else errors.push(`Not a port: "${word}"`);
    } else if (lower === 'allowed' || lower === 'denied') {
      set('result', lower, word);
    } else {
      const ref = lower.startsWith('dev:') ? lower.slice(4) : lower;
      const d = devices.find((x) => x.id === ref || x.name.toLowerCase() === ref);
      if (d) set('device', d.id, word);
      else errors.push(`Not a device, port or rule: "${word}"`);
    }
  }
  return { filters, errors };
}
